/**
 * escalation-params-api.ts — Typed API client for escalation-params admin endpoints.
 *
 * Uses SERVICES_BASE_URL + Bearer auth token (same pattern as engagement-config-page.tsx).
 * Config-only — no member PHI. No @AuditPhiAccess on fetch calls.
 *
 * Per-service admin (Epic 12.3 — betty-integrations is the 2nd mover after engagement's
 * 12.2, see [[project_rest_gateway_admin_per_service]]): all endpoints below moved from
 * the former shared top-level /admin/v1/escalation-params/* path to the per-service
 * /v1/integrations/admin/v1/escalation-params/* path (REST gateway strips
 * /v1/integrations/admin -> /admin; backend controller was admin/v1/escalation-params).
 *
 * Epic 12.9 (route hygiene): the backend controller dropped its controller-internal
 * /v1 (double-v1 client path fix — see [[project_epic12_rest_convergence_arch]]), so
 * every endpoint below now targets the single-v1
 * /v1/integrations/admin/escalation-params/* path (backend controller is now
 * admin/escalation-params).
 */

import { SERVICES_BASE_URL } from '../../lib/services-base.js';

// ─── Types (mirroring contract shapes) ───────────────────────────────────────

export type EscalationTriggerType =
  | 'threshold'
  | 'delta'
  | 'behavioral'
  | 'sdoh'
  | 'cluster'
  | 'keyword';

export type EscalationDestinationTier = 'wa' | 'rd' | 'md' | 'emergency';

export interface ThresholdConfig {
  type: 'threshold';
  sourceField: string;
  comparator: '>=' | '<=' | '>' | '<';
  value: number;
  unit: string;
}

export interface DeltaConfig {
  type: 'delta';
  sourceField: string;
  direction: 'increase' | 'decrease';
  deltaType: 'absolute' | 'relative_percent';
  magnitude: number;
  unit: string | null;
  timeWindowDays: number | null;
}

export interface BehavioralConfig {
  type: 'behavioral';
  behaviourType: string;
  consecutiveDaysThreshold: number;
}

export interface SdohConfig {
  type: 'sdoh';
  flagType: 'food_access' | 'financial_barrier';
}

export interface ClusterConfig {
  type: 'cluster';
  symptomList: string[];
  minimumCoOccurrenceCount: number;
}

export interface KeywordConfig {
  type: 'keyword';
  phrase: string;
}

export type EscalationTriggerConfig =
  | ThresholdConfig
  | DeltaConfig
  | BehavioralConfig
  | SdohConfig
  | ClusterConfig
  | KeywordConfig;

export interface EscalationTriggerRecord {
  id: string;
  name: string;
  type: EscalationTriggerType;
  config: EscalationTriggerConfig;
  destinationTier: EscalationDestinationTier;
  cooldownHours: number;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
  version: number;
  enabled: boolean;
}

export interface EscalationParamVersion {
  id: string;
  triggerId: string;
  parameterName: string;
  oldValue: unknown;
  newValue: unknown;
  author: string;
  timestamp: string;
}

export interface MdAssignmentRecord {
  staffSub: string;
  staffName: string;
  assignedBy: string;
  assignedAt: string;
}

export interface CreateTriggerRequest {
  name: string;
  type: EscalationTriggerType;
  config: EscalationTriggerConfig;
  destinationTier: EscalationDestinationTier;
  cooldownHours?: number;
  mdTierConfirmed?: boolean;
}

export interface UpdateTriggerRequest {
  name: string;
  type: EscalationTriggerType;
  config: EscalationTriggerConfig;
  destinationTier: EscalationDestinationTier;
  cooldownHours?: number;
  mdTierConfirmed?: boolean;
  enabled?: boolean;
}

// ─── API client functions ─────────────────────────────────────────────────────

function makeHeaders(accessToken: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
}

export async function listEscalationTriggers(
  accessToken: string,
): Promise<EscalationTriggerRecord[]> {
  const res = await fetch(`${SERVICES_BASE_URL}/v1/integrations/admin/escalation-params/triggers`, {
    headers: makeHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(`Failed to list triggers: ${res.status}`);
  }
  const data = (await res.json()) as { items: EscalationTriggerRecord[] };
  return data.items;
}

export async function createEscalationTrigger(
  accessToken: string,
  request: CreateTriggerRequest,
): Promise<EscalationTriggerRecord> {
  const res = await fetch(`${SERVICES_BASE_URL}/v1/integrations/admin/escalation-params/triggers`, {
    method: 'POST',
    headers: makeHeaders(accessToken),
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string };
    throw new Error(err.detail ?? `Failed to create trigger: ${res.status}`);
  }
  return (await res.json()) as EscalationTriggerRecord;
}

export async function updateEscalationTrigger(
  accessToken: string,
  triggerId: string,
  request: UpdateTriggerRequest,
): Promise<EscalationTriggerRecord> {
  const res = await fetch(
    `${SERVICES_BASE_URL}/v1/integrations/admin/escalation-params/triggers/${triggerId}`,
    {
      method: 'PUT',
      headers: makeHeaders(accessToken),
      body: JSON.stringify(request),
    },
  );
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string };
    throw new Error(err.detail ?? `Failed to update trigger: ${res.status}`);
  }
  return (await res.json()) as EscalationTriggerRecord;
}

export async function disableEscalationTrigger(
  accessToken: string,
  triggerId: string,
): Promise<EscalationTriggerRecord> {
  const res = await fetch(
    `${SERVICES_BASE_URL}/v1/integrations/admin/escalation-params/triggers/${triggerId}`,
    {
      method: 'DELETE',
      headers: makeHeaders(accessToken),
    },
  );
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string };
    throw new Error(err.detail ?? `Failed to disable trigger: ${res.status}`);
  }
  return (await res.json()) as EscalationTriggerRecord;
}

export async function enableEscalationTrigger(
  accessToken: string,
  triggerId: string,
): Promise<EscalationTriggerRecord> {
  const res = await fetch(
    `${SERVICES_BASE_URL}/v1/integrations/admin/escalation-params/triggers/${triggerId}/enable`,
    {
      method: 'POST',
      headers: makeHeaders(accessToken),
    },
  );
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string };
    throw new Error(err.detail ?? `Failed to enable trigger: ${res.status}`);
  }
  return (await res.json()) as EscalationTriggerRecord;
}

export async function getTriggerVersionHistory(
  accessToken: string,
  triggerId: string,
): Promise<EscalationParamVersion[]> {
  const res = await fetch(
    `${SERVICES_BASE_URL}/v1/integrations/admin/escalation-params/triggers/${triggerId}/history`,
    {
      headers: makeHeaders(accessToken),
    },
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch version history: ${res.status}`);
  }
  const data = (await res.json()) as { items: EscalationParamVersion[] };
  return data.items;
}

export async function getMdAssignment(accessToken: string): Promise<MdAssignmentRecord | null> {
  const res = await fetch(
    `${SERVICES_BASE_URL}/v1/integrations/admin/escalation-params/staff-config/md-assignment`,
    {
      headers: makeHeaders(accessToken),
    },
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch MD assignment: ${res.status}`);
  }
  return (await res.json()) as MdAssignmentRecord | null;
}

export async function setMdAssignment(
  accessToken: string,
  staffSub: string,
  staffName: string,
): Promise<MdAssignmentRecord> {
  const res = await fetch(
    `${SERVICES_BASE_URL}/v1/integrations/admin/escalation-params/staff-config/md-assignment`,
    {
      method: 'PUT',
      headers: makeHeaders(accessToken),
      body: JSON.stringify({ staffSub, staffName }),
    },
  );
  if (!res.ok) {
    const err = (await res.json()) as { detail?: string };
    throw new Error(err.detail ?? `Failed to set MD assignment: ${res.status}`);
  }
  return (await res.json()) as MdAssignmentRecord;
}
